
import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { useAuth } from "@/contexts/auth";
import { Trip, ItineraryEvent } from "@/types/itinerary";
import { useItineraryEvents } from "@/hooks/useItineraryEvents";
import { handleDeleteEvent } from "@/components/itinerary/ItineraryUtils";

// Component imports
import ItineraryHeader from "@/components/itinerary/ItineraryHeader";
import ItineraryContent from "@/components/itinerary/ItineraryContent";
import EventForm from "@/components/itinerary/EventForm";

interface ItineraryViewProps {
  trip: Trip | null;
  isContextView?: boolean;
  contextAuthorized?: boolean;
}

const ItineraryView: React.FC<ItineraryViewProps> = ({ 
  trip,
  isContextView = false,
  contextAuthorized
}) => {
  const { user, isLoading: authLoading } = useAuth();
  const { events, setEvents, tripDays, refreshEvents } = useItineraryEvents(trip);
  
  // Initialize selectedDate to first day of trip, or today if not available
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ItineraryEvent | null>(null);
  
  // Set initial selected date when trip data loads
  useEffect(() => {
    if (tripDays.length > 0 && !selectedDate) {
      console.log("Setting initial selected date to:", tripDays[0]);
      setSelectedDate(tripDays[0]);
    } else if (!selectedDate) {
      // Fallback to today if no trip days available
      const today = format(new Date(), 'yyyy-MM-dd');
      console.log("Fallback to today's date:", today);
      setSelectedDate(today);
    }
  }, [tripDays, selectedDate]);
  
  const handleAddEvent = () => {
    setEditingEvent(null);
    setShowForm(true);
  };
  
  const handleEditEvent = (event: ItineraryEvent) => {
    setEditingEvent(event);
    setShowForm(true);
  };
  
  const handleSelectDate = (date: string) => {
    console.log(`Date selection in ItineraryView - changing from ${selectedDate} to ${date}`);
    setSelectedDate(date);
  };
  
  const handleEventDelete = async (eventId: string) => {
    const success = await handleDeleteEvent(eventId, user?.id);
    if (success) {
      setEvents(events.filter(event => event.id !== eventId));
    }
  };
  
  const closeForm = () => {
    setShowForm(false);
    setEditingEvent(null);
    // Refresh events list when form is closed
    refreshEvents();
  };
  
  // Determine if user can edit events - either from context or using original logic
  const canEditEvents = contextAuthorized !== undefined 
    ? contextAuthorized 
    : (!authLoading && !!user && trip?.owner_id === user.id);
  
  return (
    <div className={isContextView ? "flex flex-col space-y-6" : "container mx-auto px-4 py-4"}>
      <div className="flex flex-col space-y-6">
        <ItineraryHeader 
          trip={trip} 
          canEdit={canEditEvents}
          onAddEvent={handleAddEvent}
        />
        
        <ItineraryContent
          trip={trip}
          events={events}
          selectedDate={selectedDate}
          tripDays={tripDays}
          canEditEvents={canEditEvents}
          onSelectDate={handleSelectDate}
          onEditEvent={handleEditEvent}
          onDeleteEvent={handleEventDelete}
          onAddEvent={handleAddEvent}
          setEvents={setEvents}
        />
      </div>
      
      {showForm && (
        <EventForm 
          trip={trip} 
          event={editingEvent}
          onClose={closeForm} 
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
};

export default ItineraryView;
